import React, { FC } from 'react';
import {
    SkeletonBlock,
    SkeletonFooterBar,
    SkeletonPageHeader,
    SkeletonPageShell,
    SkeletonResponsiveGrid,
    SkeletonRow,
    SkeletonStack,
    SkeletonSurface
} from 'src/components/widgets/skeleton';
import styled from 'styled-components';

// Mirrors SectionWithLeftContent: title and description on the left, the controls on the right.
const SectionRow = styled.div`
    display: flex;
    gap: 30px;
    width: 100%;
    min-width: 0;

    @media (max-width: 900px) {
        flex-direction: column;
    }
`;

const SectionLeft = styled(SkeletonStack)`
    flex: 0 0 20%;
    min-width: 0;
`;

const SectionRight = styled(SkeletonStack)`
    flex: 1 1 auto;
    min-width: 0;
`;

const LeftContentSkeleton: FC<{ $titleWidth: string }> = ({ $titleWidth }) => (
    <SectionLeft $gap='10px'>
        <SkeletonBlock $width={$titleWidth} $height='18px' />
        <SkeletonStack $gap='8px'>
            <SkeletonBlock $width='100%' $height='12px' />
            <SkeletonBlock $width='88%' $height='12px' />
            <SkeletonBlock $width='64%' $height='12px' />
        </SkeletonStack>
    </SectionLeft>
);

// The switch card carries the AI credits banner and nothing else: the features moved out into their own card.
const ToolToggleSkeleton: FC = () => (
    <SkeletonSurface $padding='24px'>
        <SkeletonStack $gap='18px'>
            <SkeletonRow $gap='12px'>
                <SkeletonBlock $width='42px' $height='22px' $radius='999px' />
                <SkeletonStack $gap='8px' $flex='1'>
                    <SkeletonBlock $width='min(300px, 70%)' $height='16px' />
                    <SkeletonBlock $width='min(460px, 90%)' $height='12px' />
                </SkeletonStack>
            </SkeletonRow>
            <SkeletonBlock $height='58px' $radius='6px' />
        </SkeletonStack>
    </SkeletonSurface>
);

const FeaturesSkeleton: FC = () => (
    <SkeletonSurface $padding='22px 24px'>
        <SectionRow>
            <LeftContentSkeleton $titleWidth='80px' />
            <SectionRight $gap='16px'>
                <SkeletonResponsiveGrid $columns='repeat(2, minmax(0, 1fr))' $gap='24px'>
                    {Array.from({ length: 2 }).map((_, index) => (
                        <SkeletonStack key={index} $gap='16px'>
                            <SkeletonBlock $width='110px' $height='18px' />
                            <SkeletonRow $gap='12px'>
                                <SkeletonBlock $width='18px' $height='18px' $radius='4px' />
                                <SkeletonBlock $width='min(220px, 90%)' $height='14px' />
                            </SkeletonRow>
                            <SkeletonStack $gap='10px'>
                                {Array.from({ length: index === 0 ? 2 : 1 }).map((_, optionIndex) => (
                                    <SkeletonRow key={optionIndex} $gap='10px'>
                                        <SkeletonBlock $width='16px' $height='16px' $radius='4px' />
                                        <SkeletonBlock $width={optionIndex % 2 === 0 ? '160px' : '130px'} $height='13px' />
                                    </SkeletonRow>
                                ))}
                            </SkeletonStack>
                            <SkeletonBlock $width='150px' $height='12px' />
                            <SkeletonBlock $width='min(220px, 100%)' $height='36px' $radius='6px' />
                        </SkeletonStack>
                    ))}
                </SkeletonResponsiveGrid>
            </SectionRight>
        </SectionRow>
    </SkeletonSurface>
);

const CheckboxListSectionSkeleton: FC<{ $titleWidth: string; $rows: number }> = ({ $titleWidth, $rows }) => (
    <SkeletonSurface $padding='22px 24px'>
        <SectionRow>
            <LeftContentSkeleton $titleWidth={$titleWidth} />
            <SectionRight $gap='16px'>
                {Array.from({ length: $rows }).map((_, index) => (
                    <SkeletonRow key={index} $gap='14px'>
                        <SkeletonBlock $width='18px' $height='18px' $radius='4px' />
                        <SkeletonStack $gap='6px' $flex='1'>
                            <SkeletonBlock $width='min(200px, 60%)' $height='14px' />
                            <SkeletonBlock $width='min(360px, 90%)' $height='12px' />
                        </SkeletonStack>
                    </SkeletonRow>
                ))}
            </SectionRight>
        </SectionRow>
    </SkeletonSurface>
);

export const AIImageGenerationPageSkeleton: FC = () => (
    <SkeletonPageShell aria-hidden='true' $padding='20px 0 92px'>
        <SkeletonPageHeader>
            <SkeletonStack $gap='8px' $flex='1'>
                <SkeletonRow $gap='10px'>
                    <SkeletonBlock $width='300px' $height='28px' />
                    <SkeletonBlock $width='82px' $height='22px' $radius='999px' />
                    <SkeletonBlock $width='64px' $height='22px' $radius='999px' />
                </SkeletonRow>
                <SkeletonBlock $width='min(680px, 100%)' $height='13px' />
                <SkeletonBlock $width='90px' $height='13px' />
            </SkeletonStack>
        </SkeletonPageHeader>
        <SkeletonStack $gap='20px'>
            <ToolToggleSkeleton />
            <FeaturesSkeleton />
            <CheckboxListSectionSkeleton $titleWidth='110px' $rows={3} />
            <CheckboxListSectionSkeleton $titleWidth='120px' $rows={2} />
            <SkeletonBlock $height='58px' $radius='6px' />
        </SkeletonStack>
        <SkeletonFooterBar>
            <SkeletonBlock $width='88px' $height='36px' $radius='6px' />
        </SkeletonFooterBar>
    </SkeletonPageShell>
);
